import { useEffect, useMemo, useState } from "react";
import api from "../../api/api";
import moment from "moment";
import { CircularProgress } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";
// Preset di range temporali rapidi.
const PRESETS = [
  { id: "7d", label: "Ultimi 7 giorni", days: 6 },
  { id: "28d", label: "Ultimi 28 giorni", days: 27 },
  { id: "3m", label: "Ultimi 3 mesi", months: 3 },
  { id: "1y", label: "Ultimo anno", years: 1 },
];

const computeRange = (preset) => {
  const today = moment();
  if (preset.days != null) {
    return {
      from: today.clone().subtract(preset.days, "days").format("YYYY-MM-DD"),
      to: today.format("YYYY-MM-DD"),
    };
  }
  if (preset.months != null) {
    return {
      from: today.clone().subtract(preset.months, "months").add(1, "day").format("YYYY-MM-DD"),
      to: today.format("YYYY-MM-DD"),
    };
  }
  if (preset.years != null) {
    return {
      from: today.clone().subtract(preset.years, "years").add(1, "day").format("YYYY-MM-DD"),
      to: today.format("YYYY-MM-DD"),
    };
  }
  return null;
};

/**
 * Tab Statistiche: aggrega le metriche SendGrid per il periodo selezionato.
 * I dati arrivano dall'endpoint /api/newsletter/stats che a sua volta consulta
 * la SendGrid Stats API (account-wide, non per singola newsletter).
 */
export const NewsletterStatistiche = () => {
  const initialRange = computeRange(PRESETS[1]); // default: ultimi 28 giorni
  const [from, setFrom] = useState(initialRange.from);
  const [to, setTo] = useState(initialRange.to);
  const [aggregatedBy, setAggregatedBy] = useState("day");
  const [presetSel, setPresetSel] = useState("28d");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ totals: {}, series: [] });
  const [errore, setErrore] = useState(null);

  const load = async (params) => {
    setLoading(true);
    setErrore(null);
    try {
      const resp = await api.get("/newsletter/stats", {
        params: {
          start_date: params?.from ?? from,
          end_date: params?.to ?? to,
          aggregated_by: params?.aggregatedBy ?? aggregatedBy,
        },
      });
      setData(resp.data || { totals: {}, series: [] });
    } catch (err) {
      console.error("Errore stats:", err);
      setErrore(
        err.response?.data?.error ||
          err.message ||
          "Errore nel caricamento delle statistiche"
      );
      setData({ totals: {}, series: [] });
    } finally {
      setLoading(false);
    }
  };

  // Auto-refresh ogni volta che cambia il range o l'aggregazione.
  // Debounce minimo per evitare richieste in cascata mentre l'utente edita le date.
  useEffect(() => {
    const t = setTimeout(() => {
      load();
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, aggregatedBy]);

  const applyPreset = (preset) => {
    const r = computeRange(preset);
    if (!r) return;
    setPresetSel(preset.id);
    setFrom(r.from);
    setTo(r.to);
  };

  const totals = data.totals || {};
  const seriesChart = useMemo(() => {
    return (data.series || []).map((d) => ({
      ...d,
      label: moment(d.date).format(
        aggregatedBy === "day" ? "DD/MM" : aggregatedBy === "week" ? "[Sett.] WW" : "MM/YYYY"
      ),
    }));
  }, [data.series, aggregatedBy]);

  const deliveryRate =
    totals.requests > 0
      ? ((totals.delivered / totals.requests) * 100).toFixed(1)
      : "0.0";
  const openRate =
    totals.delivered > 0
      ? ((totals.unique_opens / totals.delivered) * 100).toFixed(1)
      : "0.0";
  const clickRate =
    totals.delivered > 0
      ? ((totals.unique_clicks / totals.delivered) * 100).toFixed(1)
      : "0.0";
  const bounceRate =
    totals.requests > 0
      ? (((totals.bounces + totals.bounce_drops) / totals.requests) * 100).toFixed(1)
      : "0.0";

  const KpiCard = ({ label, value, sub, color = "text-slate-800" }) => (
    <div className="border rounded-lg p-3 bg-white shadow-sm flex flex-col gap-1">
      <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
      <div className={`text-2xl font-semibold ${color}`}>{value}</div>
      {sub != null && <div className="text-xs text-gray-500">{sub}</div>}
    </div>
  );

  return (
    <div className="w-full el-container p-3 flex flex-col gap-3">
      {/* Filtri: preset rapidi + range custom + aggregazione (autorefresh) */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-row flex-wrap gap-1">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => applyPreset(p)}
              className={`px-3 py-1 rounded-full text-xs border transition ${
                presetSel === p.id
                  ? "bg-miller-100 text-white border-miller-100"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex flex-col">
          <label className="text-xs text-gray-500 mb-1">Da</label>
          <input
            type="date"
            value={from}
            onChange={(e) => {
              setPresetSel(null);
              setFrom(e.target.value);
            }}
            className="border rounded px-2 py-1 text-sm"
          />
        </div>
        <div className="flex flex-col">
          <label className="text-xs text-gray-500 mb-1">A</label>
          <input
            type="date"
            value={to}
            onChange={(e) => {
              setPresetSel(null);
              setTo(e.target.value);
            }}
            className="border rounded px-2 py-1 text-sm"
          />
        </div>
        <div className="flex flex-col">
          <label className="text-xs text-gray-500 mb-1">Aggregato per</label>
          <select
            value={aggregatedBy}
            onChange={(e) => setAggregatedBy(e.target.value)}
            className="border rounded px-2 py-1 text-sm"
          >
            <option value="day">Giorno</option>
            <option value="week">Settimana</option>
            <option value="month">Mese</option>
          </select>
        </div>
        <div className="ml-auto text-xs text-gray-500 self-center flex items-center gap-2">
          {loading && <CircularProgress size={14} />}
          Sorgente: SendGrid Stats API · {seriesChart.length} punti
        </div>
      </div>

      {errore && (
        <div className="bg-red-50 border border-red-300 text-red-800 p-2 rounded text-sm">
          {errore}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <CircularProgress />
        </div>
      ) : (
        <>
          {/* KPI principali */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard
              label="Inviate"
              value={totals.requests || 0}
              sub={`${totals.processed || 0} processate da SG`}
            />
            <KpiCard
              label="Consegnate"
              value={totals.delivered || 0}
              sub={`${deliveryRate}% delivery rate`}
              color="text-emerald-700"
            />
            <KpiCard
              label="Aperture (uniche)"
              value={totals.unique_opens || 0}
              sub={`${openRate}% open rate · ${totals.opens || 0} totali`}
              color="text-blue-700"
            />
            <KpiCard
              label="Click (unici)"
              value={totals.unique_clicks || 0}
              sub={`${clickRate}% CTR · ${totals.clicks || 0} totali`}
              color="text-indigo-700"
            />
          </div>

          {/* KPI criticità */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard
              label="Bounce"
              value={(totals.bounces || 0) + (totals.bounce_drops || 0)}
              sub={`${bounceRate}% bounce rate`}
              color="text-red-700"
            />
            <KpiCard
              label="Spam report"
              value={(totals.spam_reports || 0) + (totals.spam_report_drops || 0)}
              color="text-amber-700"
            />
            <KpiCard
              label="Disiscritti"
              value={(totals.unsubscribes || 0) + (totals.group_unsubscribes || 0)}
              color="text-gray-700"
            />
            <KpiCard
              label="Bloccati / Invalidi"
              value={(totals.blocks || 0) + (totals.invalid_emails || 0)}
              color="text-orange-700"
            />
          </div>

          {/* Grafico volumi */}
          <div className="border rounded-lg bg-white p-3" style={{ height: 320 }}>
            <div className="text-sm font-semibold mb-2">Volumi per periodo</div>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={seriesChart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="delivered" name="Consegnate" fill="#10b981" />
                <Bar dataKey="bounces" name="Bounce" fill="#ef4444" />
                <Bar dataKey="spam_reports" name="Spam" fill="#f59e0b" />
                <Bar dataKey="unsubscribes" name="Unsub" fill="#6b7280" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Grafico engagement */}
          <div className="border rounded-lg bg-white p-3" style={{ height: 320 }}>
            <div className="text-sm font-semibold mb-2">Engagement (aperture e click)</div>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={seriesChart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="opens" name="Aperture" stroke="#2563eb" strokeWidth={2} />
                <Line type="monotone" dataKey="unique_opens" name="Aperture uniche" stroke="#3b82f6" strokeDasharray="4 4" />
                <Line type="monotone" dataKey="clicks" name="Click" stroke="#7c3aed" strokeWidth={2} />
                <Line type="monotone" dataKey="unique_clicks" name="Click unici" stroke="#a855f7" strokeDasharray="4 4" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="text-xs text-gray-500">
            Le statistiche sono aggregate sull'intero account SendGrid configurato
            (key <code>SENDGRID_API_KEY</code>) e non distinguono tra le singole
            newsletter. Per il dettaglio per singolo invio usa la sezione
            "Dettaglio invio" all'interno di ciascuna newsletter.
          </div>
        </>
      )}
    </div>
  );
};
